import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Fade, Popper } from "@mui/material";
import type { Instance as PopperInstance } from "@popperjs/core";
import { getPreviewImageUrl } from "../features/images/image";

const OPEN_DELAY = 250;
const PREVIEW_MAX = 260;

type HoverImageProps = {
  src: string;
  alt?: string;
  draggable?: boolean;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
};

/**
 * Product thumbnail that shows a larger preview after a short hover.
 * Renders a bare <img> so callers keep sizing it with their own `& img` styles.
 */
export const HoverImage = ({
  src,
  alt = "",
  draggable,
  onError,
}: HoverImageProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const popperRef = useRef<PopperInstance>(null);
  const timer = useRef<number>();
  const [open, setOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(() => getPreviewImageUrl(src));

  useEffect(() => {
    setPreviewSrc(getPreviewImageUrl(src));
  }, [src]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const close = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
  }, []);

  const openAfterDelay = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY);
  }, []);

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={draggable}
        onError={onError}
        onMouseEnter={openAfterDelay}
        onMouseLeave={close}
        onMouseDown={close}
      />
      <Popper
        open={open && !!src}
        anchorEl={imgRef.current}
        popperRef={popperRef}
        placement="right"
        transition
        modifiers={[
          { name: "offset", options: { offset: [0, 12] } },
          {
            name: "preventOverflow",
            options: {
              // altAxis shifts the preview up/down for rows near the top or
              // bottom edge; without it a "right" placement only slides sideways
              // and the preview gets clipped by the viewport.
              altAxis: true,
              rootBoundary: "viewport",
              padding: 12,
            },
          },
        ]}
        sx={{
          // above dialogs/popovers the picker can live inside
          zIndex: (t) => t.zIndex.tooltip,
          pointerEvents: "none",
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={120}>
            <Box
              sx={{
                p: 0.5,
                display: "flex",
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                boxShadow: 6,
              }}
            >
              <img
                src={previewSrc}
                alt={alt}
                style={{
                  display: "block",
                  maxWidth: PREVIEW_MAX,
                  maxHeight: PREVIEW_MAX,
                }}
                // Popper positions against a near-empty box while the image is
                // still loading, so it thinks the preview fits below the anchor
                // and never shifts up. Re-measure once the image has size.
                onLoad={() => popperRef.current?.update()}
                // the 400px thumb only exists for large enough sources
                onError={() => {
                  if (previewSrc !== src) setPreviewSrc(src);
                }}
              />
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
};
